// Traductions du mode Lite : pages « sessions » (liste et détail).
// Les libellés qui existent déjà dans l'app (t.session, t.sessions,
// t.sessionDetail, t.analytics) sont réutilisés tels quels par les pages.
export const fr = {
  title: "Sessions",
  subtitle: "Tes sessions de trading, leur résultat et ce qu'elles t'apprennent.",
  addPast: "Saisir une session passée",
  addPastShort: "Saisir",
  live: {
    badge: "Session en cours",
    planned: (d: string) => `Prévue : ${d}`,
    noTimer: "Sans timer",
    startMood: "Humeur au départ",
    sinceStart: "Depuis le début",
    open: "Ouvrir la session",
  },
  idle: {
    title: "Aucune session en cours",
    duration: "Durée prévue",
    minutes: "min",
  },
  summary: {
    title: "Sur la période",
    sessions: "Sessions",
    pnl: "P&L en session",
    winning: "Sessions gagnantes",
    winningHint: (w: number, n: number) => `${w} sur ${n}`,
    avgDuration: "Durée moyenne",
    totalTime: (d: string) => `${d} au total`,
    outside: "Hors session",
    outsideHint: (pct: string) => `${pct} des trades`,
    noData: "-",
  },
  history: {
    title: "Historique",
    count: (n: number) => `${n} session${n > 1 ? "s" : ""}`,
    duration: "Durée",
    durationMin: "min",
    durationMax: "max",
    minutesUnit: "min",
    showMore: (n: number) => `Afficher plus (${n} restantes)`,
    noTradesInSession: "Aucun trade",
    // Une session regroupe TOUS les comptes tradés dans le créneau : sans ce
    // repère, un trader qui importe un seul CSV croit voir des comptes fantômes.
    accountsCount: (n: number) => `${n} comptes`,
    runupShort: "Run-up",
    drawdownShort: "DD",
  },
  empty: {
    title: "Aucune session pour l'instant",
    text: "Une session regroupe ce que tu fais entre l'ouverture et la clôture de ton trading : humeur, trades, erreurs, notes. Lance la première ou saisis une session passée.",
    period: "Aucune session sur cette période",
    periodHint: "Élargis la période dans la barre de filtres.",
  },
  analysis: {
    title: "Ce que tes sessions t'apprennent",
    tabs: { mood: "Humeur", duration: "Durée", timing: "Timing", bySession: "Par session", outside: "Hors session" },
    sessionsCount: (n: number) => `${n} session${n > 1 ? "s" : ""}`,
    tradesCount: (n: number) => `${n} trade${n > 1 ? "s" : ""}`,
    winRate: "Réussite",
    wl: (w: number, l: number) => `${w} gagnants · ${l} perdants`,
    avgPerTrade: (v: string) => `${v} par trade`,
    pctOfPnl: (v: string) => `${v} du P&L total`,
  },
  detail: {
    unnamed: "Session sans nom",
    stats: {
      trades: "Trades",
      winRate: "Réussite",
      duration: "Durée",
      plannedHint: (d: string) => `prévue ${d}`,
    },
    evaluation: "Évaluation",
    moodNone: "Non renseignée",
    timing: "Horaires",
    measures: "Run-up et drawdown",
    measuresHint: "Saisis tes extrêmes de P&L latent pendant la session.",
    otherAccounts: "Trades sur un compte non affiché",
    sessionPnl: "P&L de la session",
    video: "Vidéo",
  },
};

export const en: typeof fr = {
  title: "Sessions",
  subtitle: "Your trading sessions, how they went and what they teach you.",
  addPast: "Log a past session",
  addPastShort: "Log",
  live: {
    badge: "Session in progress",
    planned: (d: string) => `Planned: ${d}`,
    noTimer: "No timer",
    startMood: "Mood at start",
    sinceStart: "Since start",
    open: "Open the session",
  },
  idle: {
    title: "No session in progress",
    duration: "Planned duration",
    minutes: "min",
  },
  summary: {
    title: "Over the period",
    sessions: "Sessions",
    pnl: "P&L in sessions",
    winning: "Winning sessions",
    winningHint: (w: number, n: number) => `${w} of ${n}`,
    avgDuration: "Average duration",
    totalTime: (d: string) => `${d} in total`,
    outside: "Outside sessions",
    outsideHint: (pct: string) => `${pct} of trades`,
    noData: "-",
  },
  history: {
    title: "History",
    count: (n: number) => `${n} session${n > 1 ? "s" : ""}`,
    duration: "Duration",
    durationMin: "min",
    durationMax: "max",
    minutesUnit: "min",
    showMore: (n: number) => `Show more (${n} left)`,
    noTradesInSession: "No trades",
    accountsCount: (n: number) => `${n} accounts`,
    runupShort: "Run-up",
    drawdownShort: "DD",
  },
  empty: {
    title: "No sessions yet",
    text: "A session groups what you do between opening and closing your trading day: mood, trades, mistakes, notes. Start your first one or log a past session.",
    period: "No sessions in this period",
    periodHint: "Widen the period in the filter bar.",
  },
  analysis: {
    title: "What your sessions teach you",
    tabs: { mood: "Mood", duration: "Duration", timing: "Timing", bySession: "Per session", outside: "Outside" },
    sessionsCount: (n: number) => `${n} session${n > 1 ? "s" : ""}`,
    tradesCount: (n: number) => `${n} trade${n > 1 ? "s" : ""}`,
    winRate: "Win rate",
    wl: (w: number, l: number) => `${w} winners · ${l} losers`,
    avgPerTrade: (v: string) => `${v} per trade`,
    pctOfPnl: (v: string) => `${v} of total P&L`,
  },
  detail: {
    unnamed: "Unnamed session",
    stats: {
      trades: "Trades",
      winRate: "Win rate",
      duration: "Duration",
      plannedHint: (d: string) => `planned ${d}`,
    },
    evaluation: "Evaluation",
    moodNone: "Not set",
    timing: "Times",
    measures: "Run-up and drawdown",
    measuresHint: "Enter your open P&L extremes during the session.",
    otherAccounts: "Trades on a hidden account",
    sessionPnl: "Session P&L",
    video: "Video",
  },
};
